import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { CenteredCardContent, StyledLinearProgressBar } from './styles'
import { formatDate } from '../../utils/utils'
import { getPercent } from '../../helpers'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import { grey } from '../../colors'
import { Divider, useTheme } from '@mui/material'
import { SlotStatus } from '../../models/DistributionPlan'
import CustomTooltip from '../../components/CustomTooltip'
import { type InstructorDistribution } from '../../models/Distribution'
import { useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'

export interface AdminViewerSurveyListCardProps {
  surveyNumber: number
  totalSurveys: number
  courseName: string
  sectionAbbr: string
  endDate: string
  startDate: string
  releaseDate: string
  studentCount: number
  completionCount: number
  slotNodeStatus: string
  slotTitle: string
  distribution: InstructorDistribution
  onClick?: () => Promise<void>
}
export const AdminViewerSurveyListCard: React.FC<AdminViewerSurveyListCardProps> = ({
  surveyNumber,
  totalSurveys,
  courseName,
  sectionAbbr,
  endDate,
  startDate,
  releaseDate,
  completionCount,
  studentCount,
  slotNodeStatus,
  slotTitle,
  distribution,
  onClick = () => { }
}): JSX.Element => {
  const navigate = useNavigate()
  const endDateStr = formatDate(endDate)
  const startDateStr = formatDate(startDate)
  const releaseDateStr = typeof releaseDate === 'string' ? releaseDate : formatDate(releaseDate)
  const percentValue = getPercent(completionCount, studentCount)
  const theme = useTheme()
  const questionCount = distribution?.academicLevelAddedQuestions?.questionCount ?? 0
  const params = new URLSearchParams({
    slotId: distribution?.slotId ?? '',
    divisionId: distribution?.division?.divisionId ?? '',
    questionCount: questionCount.toString(),
    categoryName: distribution?.division?.categoryName ?? '',
    isInstructorAddedQuestionsAllowed: distribution?.academicLevelAddedQuestions?.isCustomQuestionAllowed ? 'true' : 'false',
    addedQuestionCount: distribution?.questionCount?.toString() ?? ''
  })

  const renderSurveyDateDetails = (
    start: string,
    end: string
  ): JSX.Element => {
    try {
      switch (slotNodeStatus) {
        case SlotStatus.Draft:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              First Survey begins: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Published:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              First Survey begins: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Live:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Ends: <b>{endDateStr}</b>
            </Typography>
          )
        case SlotStatus.Closed:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Results Released: <b>{ releaseDateStr }</b>
            </Typography>
          )
        default:
          return <></>
      }
    } catch (error) {
      return <></>
    }
  }

  // Check if the division category name is included in the academic unit categories
  // This is used to determine if the "Add Questions" button should be enabled or disabled

  const isDivisionCategoryNameIncluded = distribution?.academicLevelAddedQuestions?.academicUnitCategories?.some(
    (item: { categoryName?: string }) => item?.categoryName === distribution?.division?.categoryName
  )

  const isDisabledAddQuestionButton = !distribution.isAcademicLevelAddedQuestionsAllowed ||
    slotNodeStatus === SlotStatus.Live || slotNodeStatus === SlotStatus.Closed ||
    !isDivisionCategoryNameIncluded

  const addQuestionButtonTooltipTitle = (): string => {
    if (isDisabledAddQuestionButton) {
      return 'The ability to add additional questions is disabled for this Survey'
    }

    return ''
  }

  return (
    <Card tabIndex={0} aria-label={`survey ${surveyNumber} of ${totalSurveys} ${courseName}`} style={{ marginBottom: '2rem' }}>
      {slotNodeStatus === null &&
        (
          <CenteredCardContent>
            <CircularProgress />
          </CenteredCardContent>
        )
      }

      {slotNodeStatus && (
        <>
          <CardContent >
            <Box display="flex" justifyContent="space-between" >
              <Box>
                <Typography variant="p4">
                  {slotTitle}
                </Typography>
                <Typography mt={2} variant="h5" gutterBottom>
                  {sectionAbbr}
                </Typography>
                <Typography variant="p4">
                  {courseName}
                </Typography>
              </Box>
              <Divider />
              {
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  label={'View Sections'}
                  fontWeight='600'
                  buttonSize={ButtonSize.Medium}
                  onClick={() => {
                    window.open(`${ROUTES.COURSE_AND_ENROLLMENT}/${distribution?.slotId}?navigate=adminViewerDashboard`, '_blank')
                  }}
                />
              }

            </Box>
          </CardContent>
          <CardContent aria-label={`Survey closed${percentValue}% completed ${completionCount}/${studentCount} Responses`} tabIndex={0}>
            <Box display='flex' justifyContent='space-between'>
              {renderSurveyDateDetails(startDateStr, endDateStr)}
            </Box>
            <Box padding={theme.spacing(2)}>
              <StyledLinearProgressBar value={percentValue} isNotClosed={slotNodeStatus !== SlotStatus.Closed} />
            </Box>
            <Box display='flex' justifyContent='space-between'>
              <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                {percentValue}% Complete
              </Typography>
              <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                {completionCount}/{studentCount} Responses
              </Typography>
            </Box>
            <Divider sx={{ padding: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
              <CustomTooltip title={addQuestionButtonTooltipTitle()}>
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  label={`Add Questions ${distribution?.questionCount ? ` (${distribution.questionCount})` : ''}`}
                  fontWeight='600'
                  buttonSize={ButtonSize.Medium}
                  onClick={() => {
                    navigate(`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}?${params.toString()}`)
                  }}
                  disabled={isDisabledAddQuestionButton}
                />
              </CustomTooltip>

            </Box>
          </CardContent>
        </>
      )}
    </Card>
  )
}
